<template>
  <div class="customers-page pa-6">
    <!-- Header -->
    <div class="d-flex flex-wrap align-center justify-space-between mb-6">
      <div class="d-flex align-center">
        <div class="header-icon-box me-3">
          <v-icon color="#082C69" size="28">mdi-account-group</v-icon>
        </div>
        <div>
          <h1 class="text-h5 font-weight-bold navy--text mb-0">العملاء</h1>
          <p class="text-caption text-muted mb-0">إدارة جميع العملاء ومتابعة حساباتهم</p>
        </div>
      </div>
      <div>
        <v-btn
          color="#082C69"
          class="white--text text-none font-weight-bold px-5 rounded-lg add-btn"
          elevation="1"
          @click="showAddCustomerDialog = true"
        >
          <v-icon left size="18">mdi-plus</v-icon>
          إضافة عميل جديد
        </v-btn>
      </div>
    </div>

    <!-- 4 KPI Cards -->
    <v-row class="mb-6">
      <!-- 1. Total Customers -->
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-1 rounded-xl pa-4 fill-height">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="text-subtitle-2 font-weight-bold text-muted">إجمالي العملاء</span>
            <div class="kpi-icon-circle bg-blue-light">
              <v-icon size="20" color="#082C69">mdi-account-multiple-outline</v-icon>
            </div>
          </div>
          <div class="kpi-value navy--text mb-1">
            {{ summary.total_customers }}
          </div>
          <div class="text-caption text-muted">
            جميع العملاء المسجلين
          </div>
        </v-card>
      </v-col>

      <!-- 2. Active Customers -->
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-1 rounded-xl pa-4 fill-height">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="text-subtitle-2 font-weight-bold text-muted">العملاء النشطون</span>
            <div class="kpi-icon-circle bg-green-light">
              <v-icon size="20" color="#10B981">mdi-account-check-outline</v-icon>
            </div>
          </div>
          <div class="d-flex align-baseline mb-1">
            <span class="kpi-value text-green me-2">{{ summary.active_customers }}</span>
            <v-chip x-small class="font-weight-bold cs-kpi-chip-green">
              <span class="cs-chip-text">{{ summary.active_percentage }}%</span>
            </v-chip>
          </div>
          <div class="text-caption text-muted">
            من إجمالي العملاء
          </div>
        </v-card>
      </v-col>

      <!-- 3. New This Month -->
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-1 rounded-xl pa-4 fill-height">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="text-subtitle-2 font-weight-bold text-muted">عملاء جدد هذا الشهر</span>
            <div class="kpi-icon-circle bg-amber-light">
              <v-icon size="20" color="#F59E0B">mdi-account-plus-outline</v-icon>
            </div>
          </div>
          <div class="d-flex align-baseline mb-1">
            <span class="kpi-value text-amber me-2">{{ summary.new_this_month }}</span>
            <v-chip
              v-if="summary.new_this_month_diff_percent !== undefined"
              x-small
              class="font-weight-bold cs-kpi-chip-amber"
            >
              <span class="cs-chip-text">{{ summary.new_this_month_diff_percent >= 0 ? '+' : '' }}{{ summary.new_this_month_diff_percent }}%</span>
            </v-chip>
          </div>
          <div class="text-caption text-muted">
            مقارنة بالشهر الماضي
          </div>
        </v-card>
      </v-col>

      <!-- 4. Suspended Customers -->
      <v-col cols="12" sm="6" md="3">
        <v-card class="kpi-card elevation-1 rounded-xl pa-4 fill-height">
          <div class="d-flex align-center justify-space-between mb-2">
            <span class="text-subtitle-2 font-weight-bold text-muted">عملاء موقوفون</span>
            <div class="kpi-icon-circle bg-red-light">
              <v-icon size="20" color="#EF4444">mdi-account-cancel-outline</v-icon>
            </div>
          </div>
          <div class="kpi-value text-red mb-1">
            {{ summary.suspended_customers }}
          </div>
          <div class="text-caption text-muted">
            بحاجة إلى مراجعة
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Filters Bar -->
    <v-card class="filter-card elevation-1 rounded-xl pa-4 mb-6">
      <v-row dense align="center">
        <!-- Search Input -->
        <v-col cols="12" md="4">
          <v-text-field
            v-model="filters.search"
            placeholder="البحث عن اسم العميل أو رقم الهاتف أو رقم العميل..."
            outlined
            dense
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            class="filter-field"
            @keyup.enter="applyFilters"
          ></v-text-field>
        </v-col>

        <!-- Route Filter -->
        <v-col cols="12" sm="6" md="3">
          <v-select
            v-model="filters.route_id"
            :items="routeOptions"
            item-text="name"
            item-value="id"
            outlined
            dense
            hide-details
            class="filter-field"
            prefix="المسار:"
          ></v-select>
        </v-col>

        <!-- Registration Date Filter -->
        <v-col cols="12" sm="6" md="2">
          <v-menu
            v-model="dateMenu"
            :close-on-content-click="false"
            transition="scale-transition"
            offset-y
            max-width="290px"
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="filters.date"
                placeholder="جميع التواريخ"
                outlined
                dense
                hide-details
                readonly
                v-bind="attrs"
                v-on="on"
                clearable
                prepend-inner-icon="mdi-calendar-range"
                class="filter-field"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="filters.date"
              no-title
              scrollable
              @input="dateMenu = false"
            ></v-date-picker>
          </v-menu>
        </v-col>

        <!-- Account Status Filter -->
        <v-col cols="12" sm="6" md="2">
          <v-select
            v-model="filters.status"
            :items="statusOptions"
            item-text="label"
            item-value="value"
            outlined
            dense
            hide-details
            class="filter-field"
            prefix="الحالة:"
          ></v-select>
        </v-col>

        <!-- Action Buttons -->
        <v-col cols="12" sm="6" md="1" class="d-flex align-center justify-end gap-1">
          <v-btn
            icon
            color="#64748B"
            title="إعادة ضبط"
            @click="resetFilters"
          >
            <v-icon size="20">mdi-refresh</v-icon>
          </v-btn>
          <v-btn
            color="#082C69"
            class="white--text text-none font-weight-bold px-3 rounded-lg"
            depressed
            height="38"
            @click="applyFilters"
          >
            تطبيق
          </v-btn>
        </v-col>
      </v-row>
    </v-card>

    <!-- Customers Table Card -->
    <v-card class="table-card elevation-1 rounded-xl overflow-hidden">
      <v-data-table
        :headers="headers"
        :items="customers"
        :loading="isLoading"
        loading-text="جاري تحميل بيانات العملاء..."
        no-data-text="لا يوجد عملاء يطابقون خيارات البحث"
        hide-default-footer
        class="custom-customers-table"
      >
        <!-- # Index -->
        <template v-slot:item.index="{ item, index }">
          <span class="text-caption text-muted font-weight-medium">
            {{ (pagination.current_page - 1) * pagination.per_page + index + 1 }}
          </span>
        </template>

        <!-- Customer Ref -->
        <template v-slot:item.customer_ref="{ item }">
          <span class="font-weight-bold navy--text mono-code">
            {{ item.customer_ref }}
          </span>
        </template>

        <!-- Customer Name -->
        <template v-slot:item.name="{ item }">
          <div class="d-flex align-center py-2">
            <v-avatar size="32" color="#EEF2F6" class="me-2">
              <span class="navy--text font-weight-bold text-caption customer-avatar-initials">
                {{ getInitials(item.name) }}
              </span>
            </v-avatar>
            <div>
              <div class="font-weight-bold navy--text text-body-2">{{ item.name }}</div>
              <div v-if="item.email && item.email !== '—'" class="text-caption cs-customer-email text-truncate" style="max-width: 180px;">
                {{ item.email }}
              </div>
            </div>
          </div>
        </template>

        <!-- Phone Number -->
        <template v-slot:item.phone="{ item }">
          <span dir="ltr" class="text-body-2 font-weight-bold text-dark">
            {{ item.phone }}
          </span>
        </template>

        <!-- Account Status -->
        <template v-slot:item.status_label="{ item }">
          <v-chip
            small
            class="font-weight-bold status-chip"
            :class="item.status_id === 1 ? 'cs-status-chip-active' : 'cs-status-chip-suspended'"
          >
            <span class="status-dot me-1" :class="item.status_id === 1 ? 'dot-active' : 'dot-suspended'"></span>
            <span class="cs-chip-text">{{ item.status_label }}</span>
          </v-chip>
        </template>

        <!-- Registration Date -->
        <template v-slot:item.registration_date="{ item }">
          <span class="text-caption cs-date-text">
            {{ item.registration_date }}
          </span>
        </template>

        <!-- Last Trip -->
        <template v-slot:item.last_trip_date="{ item }">
          <span v-if="item.last_trip_date !== '—'" class="text-caption font-weight-bold text-dark">
            {{ item.last_trip_date }}
          </span>
          <span v-else class="cs-dash-text">—</span>
        </template>

        <!-- Trip Count -->
        <template v-slot:item.trip_count="{ item }">
          <v-chip
            x-small
            class="font-weight-bold trips-chip"
            :class="item.trip_count > 0 ? 'cs-trips-chip-active' : 'cs-trips-chip-zero'"
          >
            <span class="cs-chip-text">{{ item.trip_count }} رحلات</span>
          </v-chip>
        </template>

        <!-- Actions -->
        <template v-slot:item.actions="{ item }">
          <div class="d-flex align-center gap-1">
            <!-- View Details Button -->
            <v-btn
              icon
              small
              color="#082C69"
              title="عرض التفاصيل"
              @click="viewCustomer(item.id)"
            >
              <v-icon size="18">mdi-eye-outline</v-icon>
            </v-btn>

            <!-- Toggle Status Button -->
            <v-btn
              icon
              small
              :color="item.status_id === 1 ? '#EF4444' : '#10B981'"
              :title="item.status_id === 1 ? 'إيقاف الحساب' : 'تنشيط الحساب'"
              @click="openStatusModal(item)"
            >
              <v-icon size="18">{{ item.status_id === 1 ? 'mdi-account-off-outline' : 'mdi-account-check-outline' }}</v-icon>
            </v-btn>
          </div>
        </template>
      </v-data-table>

      <!-- Pagination Footer -->
      <div class="d-flex flex-wrap align-center justify-space-between px-6 py-4 border-top">
        <div class="text-caption text-muted">
          عرض {{ pagination.from }} - {{ pagination.to }} من أصل {{ pagination.total }} عميل
        </div>

        <div class="d-flex align-center">
          <span class="text-caption text-muted me-2">عدد السجلات بالصفحة:</span>
          <v-select
            v-model="pagination.per_page"
            :items="[10, 25, 50]"
            dense
            outlined
            hide-details
            class="per-page-select me-4"
            @change="fetchCustomers(1)"
          ></v-select>

          <v-pagination
            v-model="pagination.current_page"
            :length="pagination.last_page"
            :total-visible="7"
            circle
            color="#082C69"
            @input="fetchCustomers"
          ></v-pagination>
        </div>
      </div>
    </v-card>

    <!-- Confirmation Modal: Toggle Customer Status -->
    <v-dialog v-model="statusModal.open" max-width="480">
      <v-card class="rounded-xl pa-4">
        <v-card-title class="d-flex align-center text-h6 font-weight-bold navy--text pb-2">
          <v-icon left :color="statusModal.targetAction === 'suspend' ? '#EF4444' : '#10B981'" size="26">
            {{ statusModal.targetAction === 'suspend' ? 'mdi-account-alert-outline' : 'mdi-account-check-outline' }}
          </v-icon>
          تغيير حالة حساب العميل
        </v-card-title>

        <v-card-text class="pt-2 pb-4">
          <p class="text-body-1 font-weight-medium text-dark mb-2">
            هل تريد بالتأكيد {{ statusModal.targetAction === 'suspend' ? 'إيقاف' : 'تنشيط' }} حساب العميل:
            <span class="navy--text font-weight-bold">{{ statusModal.customer ? statusModal.customer.name : '' }}</span>
            ({{ statusModal.customer ? statusModal.customer.customer_ref : '' }})؟
          </p>
          <div class="info-alert pa-3 rounded-lg text-caption text-muted">
            <v-icon size="16" color="#64748B" class="me-1">mdi-information-outline</v-icon>
            لن يتم حذف الحساب أو بياناته. يمكن إعادة تفعيل الحساب في أي وقت.
          </div>
        </v-card-text>

        <v-card-actions class="px-4 pb-2">
          <v-spacer></v-spacer>
          <v-btn
            text
            class="text-none font-weight-bold"
            @click="statusModal.open = false"
          >
            إلغاء
          </v-btn>
          <v-btn
            :color="statusModal.targetAction === 'suspend' ? '#EF4444' : '#10B981'"
            class="white--text text-none font-weight-bold px-4 rounded-lg"
            depressed
            :loading="statusModal.loading"
            @click="confirmStatusChange"
          >
            {{ statusModal.targetAction === 'suspend' ? 'تأكيد الإيقاف' : 'تأكيد التنشيط' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add Customer Info Dialog -->
    <v-dialog v-model="showAddCustomerDialog" max-width="500">
      <v-card class="rounded-xl pa-5">
        <v-card-title class="d-flex align-center text-h6 font-weight-bold navy--text pb-2">
          <v-icon left color="#082C69" size="26">mdi-cellphone-key</v-icon>
          إضافة عميل جديد
        </v-card-title>
        <v-card-text class="pt-2 pb-4">
          <p class="text-body-2 text-dark mb-3">
            يتم تسجيل حسابات العملاء حصرياً عبر تطبيق BeepGo للهواتف الذكية من خلال التحقق التلقائي برقم الهاتف (Firebase SMS OTP).
          </p>
          <div class="info-alert pa-3 rounded-lg text-caption text-muted">
            <v-icon size="16" color="#082C69" class="me-1">mdi-shield-check</v-icon>
            للحفاظ على أمان المنظومة وتطابق هوية الهاتف، لا يُسمح بإنشاء حسابات عملاء وهمية أو غير مؤكدة من لوحة التحكم.
          </div>
        </v-card-text>
        <v-card-actions class="px-4 pb-2">
          <v-spacer></v-spacer>
          <v-btn
            color="#082C69"
            class="white--text text-none font-weight-bold px-5 rounded-lg"
            depressed
            @click="showAddCustomerDialog = false"
          >
            حسناً
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar Notification -->
    <v-snackbar v-model="snackbar.open" :color="snackbar.color" timeout="3500" top>
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.open = false">إغلاق</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'CustomerServiceCustomersList',
  data() {
    return {
      isLoading: false,
      summary: {
        total_customers: 0,
        active_customers: 0,
        active_percentage: 0,
        new_this_month: 0,
        new_this_month_diff_percent: 0,
        suspended_customers: 0,
      },
      routes: [],
      customers: [],
      pagination: {
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1,
        from: 0,
        to: 0,
      },
      filters: {
        search: '',
        route_id: 'all',
        date: null,
        status: 'all',
      },
      dateMenu: false,
      headers: [
        { text: '#', value: 'index', sortable: false, width: '40px' },
        { text: 'رقم العميل', value: 'customer_ref', sortable: false, width: '110px' },
        { text: 'الاسم', value: 'name', sortable: false },
        { text: 'رقم الهاتف', value: 'phone', sortable: false, width: '140px' },
        { text: 'حالة الحساب', value: 'status_label', sortable: false, width: '120px' },
        { text: 'تاريخ التسجيل', value: 'registration_date', sortable: false, width: '120px' },
        { text: 'آخر رحلة', value: 'last_trip_date', sortable: false, width: '120px' },
        { text: 'عدد الرحلات', value: 'trip_count', sortable: false, width: '110px' },
        { text: 'الإجراءات', value: 'actions', sortable: false, width: '100px', align: 'center' },
      ],
      statusOptions: [
        { label: 'جميع حالات الحساب', value: 'all' },
        { label: 'نشط', value: 1 },
        { label: 'موقوف', value: 3 },
      ],
      statusModal: {
        open: false,
        customer: null,
        targetAction: 'suspend',
        loading: false,
      },
      showAddCustomerDialog: false,
      snackbar: {
        open: false,
        text: '',
        color: 'success',
      },
    };
  },
  computed: {
    routeOptions() {
      const list = Array.isArray(this.routes) ? this.routes : [];
      return [{ id: 'all', name: 'كل المسارات' }, ...list];
    },
  },
  mounted() {
    this.fetchCustomers(1);
  },
  methods: {
    async fetchCustomers(page = 1) {
      this.isLoading = true;
      try {
        const params = {
          page,
          per_page: this.pagination.per_page,
        };

        if (this.filters.search && this.filters.search.trim()) {
          params.search = this.filters.search.trim();
        }
        if (this.filters.route_id && this.filters.route_id !== 'all') {
          params.route_id = this.filters.route_id;
        }
        if (this.filters.date) {
          params.date = this.filters.date;
        }
        if (this.filters.status && this.filters.status !== 'all') {
          params.status = this.filters.status;
        }

        const response = await axios.get('/customer-service/customers', { params });
        const resData = response && response.data ? response.data : {};

        if (resData.summary) {
          this.summary = {
            total_customers: Number(resData.summary.total_customers || 0),
            active_customers: Number(resData.summary.active_customers || 0),
            active_percentage: Number(resData.summary.active_percentage || 0),
            new_this_month: Number(resData.summary.new_this_month || 0),
            new_this_month_diff_percent: resData.summary.new_this_month_diff_percent !== undefined
              ? Number(resData.summary.new_this_month_diff_percent)
              : 0,
            suspended_customers: Number(resData.summary.suspended_customers || 0),
          };
        }
        this.routes = Array.isArray(resData.routes) ? resData.routes : [];
        this.customers = Array.isArray(resData.customers) ? resData.customers : [];
        if (resData.pagination) {
          this.pagination = {
            current_page: Number(resData.pagination.current_page || 1),
            per_page: Number(resData.pagination.per_page || 10),
            total: Number(resData.pagination.total || 0),
            last_page: Number(resData.pagination.last_page || 1),
            from: Number(resData.pagination.from || 0),
            to: Number(resData.pagination.to || 0),
          };
        }
      } catch (error) {
        console.error('Error fetching customers:', error);
        this.snackbar = {
          open: true,
          text: 'فشل في تحميل قائمة العملاء',
          color: 'error',
        };
      } finally {
        this.isLoading = false;
      }
    },
    applyFilters() {
      this.fetchCustomers(1);
    },
    resetFilters() {
      this.filters = {
        search: '',
        route_id: 'all',
        date: null,
        status: 'all',
      };
      this.fetchCustomers(1);
    },
    viewCustomer(id) {
      this.$router.push(`/customer-service/customers/${id}`);
    },
    openStatusModal(customer) {
      this.statusModal.customer = customer;
      this.statusModal.targetAction = customer.status_id === 1 ? 'suspend' : 'activate';
      this.statusModal.open = true;
    },
    async confirmStatusChange() {
      if (!this.statusModal.customer) return;
      this.statusModal.loading = true;

      try {
        const customerId = this.statusModal.customer.id;
        const response = await axios.patch(`/customer-service/customers/${customerId}/status`);
        this.snackbar = {
          open: true,
          text: (response && response.data && response.data.message) || 'تم تحديث حالة العميل بنجاح',
          color: 'success',
        };
        this.statusModal.open = false;
        await this.fetchCustomers(this.pagination.current_page);
      } catch (error) {
        console.error('Error updating status:', error);
        this.snackbar = {
          open: true,
          text: (error.response && error.response.data && error.response.data.message) || 'فشل في تحديث حالة العميل',
          color: 'error',
        };
      } finally {
        this.statusModal.loading = false;
      }
    },
    getInitials(name) {
      if (!name) return 'ع';
      const parts = name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    },
  },
};
</script>

<style scoped>
.customers-page {
  font-family: 'Cairo', sans-serif !important;
  color: #1E293B;
}

.navy--text {
  color: #082C69 !important;
}

.text-muted {
  color: #475569 !important;
  opacity: 1 !important;
}

.text-dark {
  color: #0F172A !important;
  opacity: 1 !important;
}

.header-icon-box {
  width: 48px;
  height: 48px;
  background-color: #EEF2F6;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.add-btn {
  background-color: #082C69 !important;
}

.kpi-card {
  border: 1px solid #E2E8F0 !important;
  background-color: #FFFFFF !important;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04) !important;
}

.kpi-icon-circle {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bg-blue-light {
  background-color: #EFF6FF !important;
}

.bg-green-light {
  background-color: #ECFDF5 !important;
}

.bg-amber-light {
  background-color: #FFFBEB !important;
}

.bg-red-light {
  background-color: #FEF2F2 !important;
}

.kpi-value {
  font-size: 1.75rem;
  font-weight: 700;
  line-height: 1.2;
}

.text-green {
  color: #10B981 !important;
}

.text-amber {
  color: #F59E0B !important;
}

.text-red {
  color: #EF4444 !important;
}

.filter-card {
  border: 1px solid #E2E8F0 !important;
  background-color: #FFFFFF !important;
}

.filter-field ::v-deep .v-input__control .v-input__slot {
  border-radius: 8px !important;
  font-size: 0.85rem !important;
}

.table-card {
  border: 1px solid #E2E8F0 !important;
  background-color: #FFFFFF !important;
}

.custom-customers-table ::v-deep th {
  background-color: #F8FAFC !important;
  color: #475569 !important;
  font-weight: 700 !important;
  font-size: 0.8rem !important;
  border-bottom: 1px solid #E2E8F0 !important;
  height: 48px !important;
}

.custom-customers-table ::v-deep td {
  border-bottom: 1px solid #F1F5F9 !important;
  height: 56px !important;
}

.mono-code {
  font-family: 'Roboto Mono', monospace;
  font-size: 0.85rem;
}

.status-chip {
  font-size: 0.75rem !important;
  height: 24px !important;
}

.status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  display: inline-block;
}

.dot-active {
  background-color: #10B981;
}

.dot-suspended {
  background-color: #EF4444;
}

.border-top {
  border-top: 1px solid #E2E8F0;
}

.per-page-select {
  max-width: 80px;
}

.per-page-select ::v-deep .v-input__control .v-input__slot {
  min-height: 32px !important;
  height: 32px !important;
  padding: 0 8px !important;
}

.info-alert {
  background-color: #F8FAFC;
  border: 1px solid #E2E8F0;
}

.gap-1 {
  gap: 4px;
}

.gap-2 {
  gap: 8px;
}

/* Customer Service Badges Text Clarity & Visibility */
.cs-status-chip-active {
  background-color: #ECFDF5 !important;
  border: 1px solid #A7F3D0 !important;
}
.cs-status-chip-active ::v-deep .v-chip__content,
.cs-status-chip-active .cs-chip-text {
  color: #065F46 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-status-chip-suspended {
  background-color: #FEF2F2 !important;
  border: 1px solid #FECACA !important;
}
.cs-status-chip-suspended ::v-deep .v-chip__content,
.cs-status-chip-suspended .cs-chip-text {
  color: #991B1B !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-trips-chip-active {
  background-color: #EFF6FF !important;
  border: 1px solid #BFDBFE !important;
}
.cs-trips-chip-active ::v-deep .v-chip__content,
.cs-trips-chip-active .cs-chip-text {
  color: #1E40AF !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-trips-chip-zero {
  background-color: #F1F5F9 !important;
  border: 1px solid #E2E8F0 !important;
}
.cs-trips-chip-zero ::v-deep .v-chip__content,
.cs-trips-chip-zero .cs-chip-text {
  color: #334155 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-kpi-chip-green {
  background-color: #ECFDF5 !important;
  border: 1px solid #A7F3D0 !important;
}
.cs-kpi-chip-green ::v-deep .v-chip__content,
.cs-kpi-chip-green .cs-chip-text {
  color: #065F46 !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.cs-kpi-chip-amber {
  background-color: #FFFBEB !important;
  border: 1px solid #FDE68A !important;
}
.cs-kpi-chip-amber ::v-deep .v-chip__content,
.cs-kpi-chip-amber .cs-chip-text {
  color: #92400E !important;
  font-weight: 700 !important;
  opacity: 1 !important;
}

.customer-avatar-initials {
  color: #082C69 !important;
  font-weight: 700 !important;
}

.cs-customer-email {
  color: #475569 !important;
  font-weight: 500 !important;
  opacity: 1 !important;
}

.cs-date-text {
  color: #334155 !important;
  font-weight: 600 !important;
  opacity: 1 !important;
}

.cs-dash-text {
  color: #94A3B8 !important;
  font-weight: 700 !important;
}
</style>
